import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';
import {
  captiveApi,
  type CaptiveAccountState,
  type CaptiveActivationOptions,
  type CaptiveAtLimitDetails,
  type CaptiveAuth,
  type CaptiveContext,
  type CaptiveDevice,
  type CaptiveInsufficientBalanceDetails,
  type CaptiveRegisterResult,
  type CaptiveState,
  type CaptiveStaticAddress,
  registrationIsBound,
} from '../api/captive';
import { connectionFields, readConnection, type Connection } from './connection';
import { absMoney, formatRubles, isNegativeMoney, isPositiveMoney } from './format';
import { PortalShell } from './PortalShell';
import { LoginScreen } from './LoginScreen';
import { RegisteringScreen } from './RegisteringScreen';
import { AddressChoiceScreen } from './AddressChoiceScreen';
import { DeviceChoiceScreen } from './DeviceChoiceScreen';
import { ConfirmationScreen } from './ConfirmationScreen';
import { BlockedScreen } from './BlockedScreen';
import { ActivationScreen } from './ActivationScreen';
import { StateUnavailableScreen } from './StateUnavailableScreen';
import { SupportScreen, type SupportVariant } from './SupportScreen';

/**
 * Screens of the flow. `blocked` is the FINANCIAL block screen (BlockedScreen) —
 * it is a local screen id, not the removed `blocked` wire state, whose three
 * successors (`financial_block`, `admin_block`, `disabled`) map to `blocked` and
 * `support` here.
 */
type Step =
  | 'login'
  /**
   * The STATIC-ADDRESS choice (customer request 2026-08-17). Entered between the
   * login and the automatic registration, and ONLY for an account that holds at
   * least one assigned, unused static address — everybody else goes straight to
   * `registering`, exactly as before, and sees no control at all.
   */
  | 'address'
  | 'registering'
  | 'choice'
  | 'confirmed'
  | 'activation'
  | 'blocked'
  | 'support'
  /** `state: "unknown"` — billing could not say; re-poll rather than guess. */
  | 'unavailable';

/**
 * THE ACCOUNT STATES A *FAILED* REGISTRATION MAY ROUTE ON (D408, production
 * 2026-09-01).
 *
 * ⚠ THE DEFECT THIS CONSTANT EXISTS FOR. `runRegister`'s catch used to end at
 * `setRegisterError(message)`, and `routeState` was reachable ONLY from
 * `applyResult` — i.e. only on a SUCCESSFUL registration. Every refusal was
 * therefore a DEAD END: the subscriber sat on the registration screen with a
 * «Повторить» button and no way to learn anything at all about his account.
 * Production ran 1582 × `404 captive.context_not_found` against 14 successes from
 * 2026-08-31 21:00 UTC, because the month boundary financially suspended ~417
 * accounts and a suspended subscriber sits in the walled garden holding his ordinary
 * PUBLIC address — which has no captive lease, so `POST /captive/register` cannot
 * succeed for him at all. What he was shown was «не найдена активная сессия захвата
 * … перезагрузите устройство»: advice that cannot possibly help, because his problem
 * is that he owes money.
 *
 * THE RULE IS A JUDGEMENT CALL between two candidates:
 *
 *   (a) fall back only for `captive.context_not_found`, the one code production is
 *       emitting; or
 *   (b) fall back for ANY registration refusal whenever the state we ALREADY HOLD
 *       (the login response's own account block) is one that blocks service.
 *
 * ⚠ (b) IS WHAT IS BUILT. The defect is STRUCTURAL rather than specific to one code
 * — the catch had no path to `routeState` at all — so narrowing to
 * `context_not_found` would repair exactly today's 404s and leave the identical dead
 * end for the same suspended subscriber behind a 500, a 429, a transport failure, or
 * the next code the core learns to send; he would again be invited to retry his way
 * out of a debt. And for an account in one of THESE three states, nothing the
 * registration does or fails to do changes what he must do next: pay
 * (`financial_block`) or reach an operator (`admin_block`, `disabled`). The account
 * state outranks the refusal precisely because it IS his next action.
 *
 * ⚠ AND IT IS DELIBERATELY NOT WIDER THAN THESE THREE. `serviceable` and
 * `awaiting_activation` KEEP the error and the retry button, because for them the
 * registration IS the next action — «a device with no valid binding stays in the
 * captive `invalid_creds` state, so activating the account alone would still leave it
 * in the walled garden» (the flow comment below). Routing a serviceable subscriber to
 * ConfirmationScreen after a failed registration would promise «устройство
 * зарегистрировано … доступ откроется» for a device that was never bound, which is
 * the same false-promise shape this portal has already had to fix twice
 * (`devices.mac_bound_to_another_account`, «Состояние счёта временно недоступно»).
 * `unknown` is not here either: it means billing could not establish a state, so
 * there is nothing to route ON — and StateUnavailableScreen's own copy asserts the
 * device was registered.
 *
 * ⚠ THE REFUSAL'S OWN MESSAGE IS NOT CARRIED ONTO THE STATE SCREEN. The customer's
 * requirement is that this population stops seeing these errors at all («if user
 * comes from ISP address (either public or captive) there should not be such errors,
 * since we always can identify him»), and every one of those messages describes an
 * action that is not his next one. What IS carried is the honest fact that the
 * binding was not confirmed: `blocked.deviceUnconfirmed` / `support.deviceUnconfirmed`
 * REPLACE the «устройство уже зарегистрировано» line those screens would otherwise
 * show, because saying that after a failed registration would be a lie. Nor is the
 * refusal lost for good — the moment the block is lifted the state stops being a
 * blocking one, and the next registration surfaces the refusal normally.
 */
const BLOCKING_STATES: ReadonlySet<CaptiveState> = new Set<CaptiveState>([
  'financial_block',
  'admin_block',
  'disabled',
]);

/**
 * WHY A SERVICEABLE SUBSCRIBER CAN REACH THE END OF THE FLOW HAVING REGISTERED
 * NOTHING — and what he is shown when he does *(added 2026-09-01 with the backend
 * half of D408)*.
 *
 *   'registered'  — a binding was written (or was already valid). The screen may say
 *                   so, and may promise that access is opening.
 *   'activated'   — the self-activation branch: the ACCOUNT changed, verified by the
 *                   core's own `activated` flag.
 *   'no_action'   — `outcome: "no_device_identity"` on a `serviceable` account: he is
 *                   on an address this ISP owns that no captive lease backs, so there
 *                   was nothing to register and this call changed nothing.
 *
 * ⚠ 'no_action' IS A DELIBERATE THIRD ANSWER RATHER THAN AN ERROR OR A RETRY, and the
 * reasoning is the mirror image of BLOCKING_STATES' above. On the FAILURE path this
 * flow keeps the error and the retry for `serviceable`, because there the refusal was
 * unexplained and a retry might genuinely bind the device. Here the core has told us
 * positively what happened, and the answer is DETERMINISTIC: the same request from
 * the same address will return `no_device_identity` for as long as he holds that
 * address. A «Повторить» button that re-asks a settled question is the same false
 * promise this portal removed from `devices.mac_bound_to_another_account` — help
 * offered on a condition the button cannot change.
 *
 * ⚠ AND «доступ откроется» IS NOT AVAILABLE EITHER — the server's own constant block
 * says so in as many words. Nothing opened; nothing was closed. What is TRUE is that
 * his account is serviceable and his connection already holds an ordinary (non-garden)
 * address, so there is nothing for him to do on this page at all. That is what he is
 * told, with the one honest caveat: if the internet nevertheless does not work, the
 * operator can see this connection, because it IS one of ours — which is precisely
 * what the 200 proved.
 */
type ConfirmVariant = 'registered' | 'activated' | 'no_action';

/**
 * The subscriber flow (Amendment 02 Part A + self-activation), a small state
 * machine over the subscriber endpoints of internal/captive:
 *
 *   login → POST /captive/login
 *     → GET /captive/static-addresses      (an account that holds an unused static
 *       → AddressChoiceScreen               address chooses one here, or keeps the
 *                            ↓              dynamic default; nobody else sees a step)
 *     → auto POST /captive/register        (ALWAYS: the device binding is what makes
 *       success            ↓                the NEXT authorization resolve the account;
 *       409 at-limit       → GET /captive/devices → DeviceChoiceScreen
 *                            → POST /captive/register {replace_id, static_lease_id?} ↓
 *     → branch on the account `state`:
 *       serviceable         → ConfirmationScreen (wait-for-renewal)
 *       awaiting_activation → ActivationScreen  (activate / top up)
 *       financial_block     → BlockedScreen     (replenishment notice)
 *       admin_block         → SupportScreen     (administrative block)
 *       disabled            → SupportScreen     (account switched off)
 *
 * Registration comes FIRST for every state on purpose: a device with no valid
 * binding stays in the captive `invalid_creds` state, so activating the account
 * alone would still leave it in the walled garden.
 *
 * The connecting device is identified by the session's captive lease, which the
 * core resolves from the REQUEST CONNECTION itself (`?ip=`/`?vlan=` are an
 * optional override — see connection.ts); the portal never asks for device data
 * and never blocks enrollment on a missing address (D23).
 */
export function EnrollmentFlow() {
  const { t } = useTranslation();
  const connection = useMemo<Connection>(() => readConnection(window.location.search), []);

  const [step, setStep] = useState<Step>('login');
  const [context, setContext] = useState<CaptiveContext | null>(null);
  const [contextResolved, setContextResolved] = useState(false);
  const [auth, setAuth] = useState<CaptiveAuth | null>(null);

  const [loginSubmitting, setLoginSubmitting] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const [registerError, setRegisterError] = useState<string | null>(null);
  const [registerRetrying, setRegisterRetrying] = useState(false);

  /**
   * DID A REGISTRATION ACTUALLY SUCCEED? (D408.)
   *
   * Before the state fallback below existed, every screen after `registering` was
   * reachable only through `applyResult`, so «устройство зарегистрировано» was true
   * by construction and three screens say it in so many words. It is no longer true
   * by construction: a blocked subscriber now reaches BlockedScreen / SupportScreen
   * with NO binding written, and after paying he reaches ConfirmationScreen the same
   * way. Those three screens take this flag and swap the claim for an honest one —
   * the point of the fix is to stop showing him something that cannot be acted on,
   * which a false «уже зарегистрировано» would re-create in a nicer typeface.
   *
   * A 200 with NO `device` still counts as confirmed: the core answers that for a
   * non-serviceable account whose MAC is ALREADY bound (`macAlreadyBound` in
   * internal/captive), i.e. there was nothing to register.
   *
   * ⚠ PLAIN STATE, NOT A REF — unlike `selectedStaticRef` two blocks down, which is a
   * ref precisely because `runRegister` reads it at CALL time. Nothing reads this one
   * inside a callback; it is only ever rendered. Mirroring it into a ref «for
   * symmetry» would add a second copy that can only ever drift.
   */
  const [registrationConfirmed, setRegistrationConfirmed] = useState(false);

  const [choice, setChoice] = useState<CaptiveAtLimitDetails | null>(null);
  const [choiceSubmitting, setChoiceSubmitting] = useState(false);
  const [choiceError, setChoiceError] = useState<string | null>(null);

  const [registeredDevice, setRegisteredDevice] = useState<CaptiveDevice | undefined>(undefined);

  /**
   * THE STATIC-ADDRESS CHOICE (customer request 2026-08-17).
   *
   * `statics` is what the account actually holds — an EMPTY list means «render no
   * control», which is why the step is skipped rather than shown empty.
   * `selectedStatic` is null for the dynamic default and stays null unless the
   * subscriber picks a row: nothing auto-selects, not even a lone address.
   *
   * ⚠ IT IS A REF AS WELL AS STATE, and that is load-bearing rather than tidy.
   * `runRegister` is a useCallback and reads the choice at CALL time; a plain state
   * value would be captured in the closure at the render the callback was built in,
   * so the retry button and the at-limit replacement path — both of which re-enter
   * registration after further renders — could send a stale value. The register
   * bodies are built from the ref for that reason.
   */
  const [statics, setStatics] = useState<CaptiveStaticAddress[]>([]);
  const [selectedStatic, setSelectedStatic] = useState<number | null>(null);
  const [addressError, setAddressError] = useState<string | null>(null);
  const selectedStaticRef = useRef<number | null>(null);
  selectedStaticRef.current = selectedStatic;

  // Account-state block from /captive/login, refreshed by /captive/account.
  const [account, setAccount] = useState<CaptiveAccountState | null>(null);
  const [supportVariant, setSupportVariant] = useState<SupportVariant>('admin_block');
  const [confirmVariant, setConfirmVariant] = useState<ConfirmVariant>('registered');

  // The plan choice, when the account activates out of a tariff group (2026-08-16).
  // `options` is fetched when the activation screen is entered and re-fetched after a
  // refresh, NOT on the 30 s poll: it costs one activation quote per candidate plan.
  const [options, setOptions] = useState<CaptiveActivationOptions | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [selectedTariff, setSelectedTariff] = useState<number | null>(null);

  const [activating, setActivating] = useState(false);
  const [activationError, setActivationError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [topUpPending, setTopUpPending] = useState(false);
  const [topUpNotice, setTopUpNotice] = useState<string | null>(null);

  // Keep the freshest token / account for callbacks without re-creating them.
  const authRef = useRef<CaptiveAuth | null>(null);
  authRef.current = auth;
  const accountRef = useRef<CaptiveAccountState | null>(null);
  accountRef.current = account;

  // Best-effort DISPLAY ONLY: resolve the connecting device's identity. The call
  // carries the URL override when there is one and nothing otherwise — the core
  // then reads the lease of the connection's own address. A failure (transport
  // error, throttle) only hides the panel and shows a muted hint; it never blocks
  // login or registration, because the core knows the identity from the lease
  // regardless of what this read-out managed to fetch.
  //
  // ⚠ «NO LIVE CAPTIVE LEASE» IS NO LONGER A FAILURE *(2026-08-22, the customer's
  // request)*. It used to be a `404`, i.e. the `.catch()` below, and that silenced
  // the page for the subscriber an operator is most likely to be phoning about — a
  // suspended one in the walled garden holds his ordinary PUBLIC address, and a
  // static/unclassified one has no captive lease at all (D324). The core now answers
  // `200` with `client_ip` and an empty `mac`, which reaches the `.then()`; the
  // screen shows the address AND the muted hint about the unidentified device.
  useEffect(() => {
    let cancelled = false;
    captiveApi
      .context(connectionFields(connection))
      .then((ctx) => {
        if (!cancelled) setContext(ctx);
      })
      .catch(() => {
        /* identity read-out is optional */
      })
      .finally(() => {
        if (!cancelled) setContextResolved(true);
      });
    return () => {
      cancelled = true;
    };
  }, [connection]);

  /**
   * The refusals that are about THE ADDRESS rather than about the registration.
   *
   * They share one property that decides how the flow must answer them: re-sending
   * the identical request cannot succeed. `ippool.static_not_of_account` and
   * `ippool.lease_not_found` mean the address stopped being his (an operator
   * re-assigned or released it while this screen was open — genuinely reachable now
   * that there IS a screen); `ippool.static_already_bound` and
   * `ippool.device_static_conflict` mean it cannot go on THIS device; and the 503
   * means the build binds no addresses at all.
   *
   * ⚠ SO THE PICK IS DROPPED AND THE SUBSCRIBER GOES BACK TO THE CHOOSER. Leaving him
   * on «Повторить» with the refused id still in hand is the same defect this portal
   * fixed for `devices.mac_bound_to_another_account` the same morning: a button that
   * promises a retry helps, on a condition a retry cannot change. Every one of these
   * refusals already says «выберите адрес заново или зарегистрируйте устройство без
   * выбора адреса» — this is the flow doing what the copy says.
   */
  const addressRefusals = useMemo(
    () =>
      new Set([
        'ippool.static_not_of_account',
        'ippool.lease_not_found',
        'ippool.static_already_bound',
        'ippool.device_static_conflict',
        'devices.static_address_unavailable',
      ]),
    [],
  );

  // Distinguishable failure copy: no captive lease vs. an address that is not this
  // device's vs. transport/server trouble (the operator has to tell them apart on
  // a subscriber's phone call).
  const registerErrorMessage = useCallback(
    (err: unknown): string => {
      if (err instanceof ApiError) {
        if (err.isNetworkError) return t('register.networkError');
        switch (err.code) {
          // The core found no `issued` captive lease for this address: the lease
          // expired, or this client is not actually in a captive session.
          case 'captive.context_not_found':
            return t('register.noLease');
          // The portal was opened with an address that is not the one this device
          // connects from, so the enrollment cannot be bound to it (a device may
          // only ever register its own identity).
          case 'captive.source_ip_mismatch':
            return t('register.sourceIpMismatch');
          case 'captive.invalid_source_ip':
            return t('register.invalidSourceIp');
          // The MAC is already registered to ANOTHER account. Permanent until an
          // operator unbinds it, so the copy must not offer a retry — and it must
          // not name the holder, which is why the core sends this code with no
          // details at all (D330).
          case 'devices.mac_bound_to_another_account':
            return t('register.boundElsewhere');
          // ---- The static-address choice (customer request 2026-08-17) ----
          //
          // ⚠ THESE FIVE CODES ARE LIVE. `GET /captive/static-addresses` lists the
          // token account's own unbound statics, `AddressChoiceScreen` offers them and
          // sends the chosen `lease_id` as `static_lease_id`, and the branch below
          // `addressRefusals` puts the subscriber back on the chooser with the reason.
          // The core sends every one of them WITHOUT details (D330), so none of the
          // copy names an address, a device or an account — and none of them is fixed
          // by re-sending the identical request, which is why none may fall through to
          // «Попробуйте ещё раз».
          //
          // ~~⚠ THESE FIVE CODES ARE UNREACHABLE FROM THIS APP TODAY AND ARE MAPPED
          // ANYWAY. `static_lease_id` is a real, landed field of POST
          // /captive/register, but the portal has no authorised route that lists the
          // account's unbound statics, so it never sends one and the core never runs
          // the bind these refusals come from (see CaptiveRegisterInput.static_lease_id
          // for the finding). They are mapped for the same reason
          // `captive.invalid_source_ip` above is — a code the server documents on a
          // route this app calls must not fall through to «Попробуйте ещё раз», which
          // is a false promise for every one of them … for a build that will never do
          // this.~~ STRUCK IN PLACE 2026-08-18, when the missing route was built — the
          // same day and the same strike as the sibling note on
          // `CaptiveRegisterInput.static_lease_id` in api/captive.ts, which this one
          // pointed at. It is kept because its ARGUMENT still holds — mapping a code
          // this app can provoke is not optional — and because a vanished note invites
          // the same edit again, while a struck one teaches.
          case 'ippool.static_not_of_account':
          // 404, and the same thing from the subscriber's side: the address he picked
          // is not a reservation of his any more.
          case 'ippool.lease_not_found':
            return t('register.staticNotYours');
          case 'ippool.static_already_bound':
            return t('register.staticAlreadyBound');
          case 'ippool.device_static_conflict':
            return t('register.staticDeviceConflict');
          // 503 — matched by CODE, and it must stay in this switch: the status check
          // below would otherwise answer the generic «Сервер временно недоступен»,
          // i.e. "wait and retry", for a build that will never do this.
          case 'devices.static_address_unavailable':
            return t('register.staticUnavailable');
          case 'captive.not_wired':
            return t('register.serverError');
        }
        // 429: the portal throttles per device address (a re-drivable /register is
        // otherwise free to hammer the core and re-send a teardown per call).
        if (err.status === 429) return t('common.tooManyRequests');
        if (err.status >= 500) return t('register.serverError');
      }
      return t('register.genericError');
    },
    [t],
  );

  /**
   * Copy for the activation calls (`/activate`, `/account`).
   *
   * The core's error `message` is the bare machine code — internal/i18n has no
   * `captive.*` entries — so it is NEVER rendered: every documented code maps to
   * portal copy here, and an unrecognised one falls back to `fallbackKey`.
   * `captive.insufficient_balance` is not here because it needs the shortfall
   * interpolated; `captive.unauthorized` (401) restarts the login instead.
   */
  const activationErrorMessage = useCallback(
    (err: unknown, fallbackKey: string): string => {
      if (err instanceof ApiError) {
        if (err.isNetworkError) return t('common.networkError');
        switch (err.code) {
          case 'captive.activation_not_available':
            return t('activation.notAvailable');
          // A group account's activation reached the core with no plan (2026-09-24; the
          // core answered 500 before). The screen guards it, so this is a stale screen.
          case 'captive.tariff_choice_required':
            return t('planChoice.selectFirst');
          // No credential checker installed on the core — nothing the subscriber
          // can do, and not his fault.
          case 'captive.not_wired':
            return t('common.serviceUnavailable');
        }
        if (err.status === 429) return t('common.tooManyRequests');
        if (err.status >= 500) return t('common.serviceUnavailable');
      }
      return t(fallbackKey);
    },
    [t],
  );

  /**
   * Copy for `/captive/topup`. Every outcome is a calm notice pointing at the
   * working office/ОСМП route: in this release the endpoint is a stub, so "card
   * payment is unavailable" is an EXPECTED answer, not a failure.
   */
  const topUpNoticeMessage = useCallback(
    (err: unknown): string => {
      if (err instanceof ApiError) {
        if (err.isNetworkError) return t('common.networkError');
        if (err.status === 429) return t('common.tooManyRequests');
        if (err.code === 'captive.topup_unavailable' || err.status === 503) {
          return t('topup.unavailable');
        }
        if (err.code === 'captive.invalid_amount') {
          const max = (err.details as { max?: string } | undefined)?.max;
          return t('topup.invalidAmount', { max: formatRubles(max) });
        }
      }
      // captive.topup_failed (502) and anything unrecognised.
      return t('topup.error');
    },
    [t],
  );

  // A 401 on a state-changing call means the short-lived portal token expired;
  // retrying in place can only fail again, so the flow returns to the login screen.
  const isSessionExpired = (err: unknown): boolean =>
    err instanceof ApiError && err.status === 401;

  /**
   * The single routing decision of the flow: the account state → its screen. Only
   * `serviceable` means "you have internet now"; every other value is a screen that
   * explains what is missing and who can fix it.
   *
   * `serviceable` is matched EXPLICITLY and everything unrecognised falls to the
   * degraded retry screen. The confirmation screen promises that access is opening,
   * so it must only ever be reached on a state the core positively called
   * serviceable — a `default:` that landed there would re-create, in the SPA, exactly
   * the fail-open the core's `unknown` state exists to prevent.
   *
   * `variant` lets a caller say WHY the account became serviceable: an activation
   * observed by the re-poll (a manager activated it while the subscriber waited)
   * deserves the "service activated" copy, not "device registered".
   */
  const routeState = useCallback(
    (state: CaptiveState | undefined, variant: ConfirmVariant = 'registered') => {
      switch (state) {
        case 'serviceable':
          setConfirmVariant(variant);
          setStep('confirmed');
          return;
        case 'awaiting_activation':
          setStep('activation');
          return;
        case 'financial_block':
          setStep('blocked');
          return;
        case 'admin_block':
          setSupportVariant('admin_block');
          setStep('support');
          return;
        case 'disabled':
          setSupportVariant('disabled');
          setStep('support');
          return;
        default: // 'unknown' and anything a future core adds
          setStep('unavailable');
      }
    },
    [],
  );

  // Drop the expired token and send the subscriber back to the login screen.
  const restartLogin = useCallback(() => {
    authRef.current = null;
    setAuth(null);
    // The address offer belonged to the PREVIOUS session's account.
    setStatics([]);
    setSelectedStatic(null);
    setAddressError(null);
    selectedStaticRef.current = null;
    setChoice(null);
    setChoiceError(null);
    setRegisterError(null);
    setRegistrationConfirmed(false);
    setActivationError(null);
    setTopUpNotice(null);
    setLoginError(t('register.sessionExpired'));
    setStep('login');
  }, [t]);

  /**
   * Apply a register result. The response carries the whole account-state block — one
   * round trip fresher than the login body's — which decides where the subscriber
   * goes next. Merged over what login reported so a field the core did not send
   * cannot blank a known one.
   *
   * ~~«Apply a SUCCESSFUL register result: the device is bound»~~ STRUCK IN PLACE
   * 2026-09-01, when the backend half of D408 landed. That sentence was true for as
   * long as any address with no captive lease was answered `404`; since the core
   * answers `200` for EVERY address inside a pool this ISP declared, a 200 no longer
   * means a device was bound, and `outcome` is the field that says which happened. It
   * is struck rather than deleted because the assumption is the one a future edit
   * would make again — the whole method reads like a success handler.
   *
   * ⚠ SO `registrationConfirmed` COMES FROM THE OUTCOME, NEVER FROM THE MERE FACT OF
   * A 200. Getting this wrong re-opens, through the SUCCESS path, exactly the
   * comfortable falsehood the failure path was fixed to avoid: a `serviceable`
   * subscriber on an ISP address with no captive lease would be routed to
   * ConfirmationScreen and promised «Устройство зарегистрировано … доступ откроется»
   * for a device nothing bound. `registrationIsBound` fails towards «not bound» for a
   * missing or unrecognised value, which is the safe direction.
   */
  const applyResult = useCallback(
    (res: CaptiveRegisterResult) => {
      const { device, outcome, ...block } = res;
      if (device) setRegisteredDevice(device);
      const bound = registrationIsBound(outcome);
      setRegistrationConfirmed(bound);
      const state: CaptiveAccountState = { ...accountRef.current, ...block };
      accountRef.current = state;
      setAccount(state);
      // A serviceable subscriber whose call bound nothing gets the third confirmation
      // shape (see ConfirmVariant); every other state ignores the variant and routes
      // on itself.
      routeState(state.state, bound ? 'registered' : 'no_action');
    },
    [routeState],
  );

  /**
   * The static-address half of a register body, and it is a SEPARATE FUNCTION so the
   * two call sites cannot disagree.
   *
   * ⚠ IT RETURNS AN EMPTY OBJECT WHEN NOTHING WAS CHOSEN, never `{static_lease_id:
   * null}`. The absence of the key is what makes a registration dynamic: the Go field
   * is a `*int64`, and while JSON `null` also decodes to nil today, a key that is
   * present is one default away from carrying a number. The portal's own tests assert
   * the key SET of the body for that reason.
   */
  const staticField = useCallback(
    (): { static_lease_id?: number } =>
      selectedStaticRef.current == null ? {} : { static_lease_id: selectedStaticRef.current },
    [],
  );

  /**
   * Read the account's own unused static addresses and decide whether there is a
   * choice to make at all (customer request 2026-08-17).
   *
   * ⚠ IT FAILS TOWARDS THE OLD BEHAVIOUR, deliberately. A transport failure, a 429,
   * or a core with no address reader (`503 captive.not_wired`) all mean the same
   * thing to the SUBSCRIBER — no choice can be offered — and registration must not
   * be blocked by a read that is a convenience. So anything but a non-empty list
   * goes straight to the automatic registration, which is byte-for-byte what this
   * portal did before the feature existed. The core keeps 503 and «[]» distinguishable
   * on its side, so an OPERATOR can still tell a lost wiring from an empty estate
   * (internal/app's TestCaptiveStaticAddressPortIsWiredInARealBoot).
   */
  const offerAddressChoice = useCallback(async (): Promise<boolean> => {
    const token = authRef.current;
    if (!token) return false;
    try {
      const list = await captiveApi.staticAddresses(token);
      const items = list?.items ?? [];
      setStatics(items);
      // NOTHING is pre-selected — not even when there is exactly one address. See
      // AddressChoiceScreen for why this differs from the plan chooser.
      setSelectedStatic(null);
      selectedStaticRef.current = null;
      return items.length > 0;
    } catch {
      setStatics([]);
      setSelectedStatic(null);
      selectedStaticRef.current = null;
      return false;
    }
  }, []);

  /**
   * THE FALLBACK THAT MAKES A FAILED REGISTRATION STOP BEING A DEAD END (D408).
   *
   * The login response already carried the whole account-state block, so at the
   * moment `/register` refuses we ALREADY KNOW what the subscriber has to do. When
   * that state is one the registration cannot change for him (see BLOCKING_STATES
   * for the rule and why it is not narrowed to a single error code), route on it and
   * report that nothing was bound; the caller then returns instead of setting an
   * error message.
   *
   * It returns a boolean rather than routing unconditionally so that each call site
   * keeps its own, better-specific handling — the device chooser, the address
   * chooser, the login restart — all of which run BEFORE this.
   */
  const routeOnKnownBlockingState = useCallback((): boolean => {
    const known = accountRef.current?.state;
    if (!known || !BLOCKING_STATES.has(known)) return false;
    setRegistrationConfirmed(false);
    // Nothing about the refusal survives onto the state screen; the screens carry
    // the honest «регистрация не подтверждена» line instead.
    setRegisterError(null);
    setChoiceError(null);
    setChoice(null);
    setAddressError(null);
    routeState(known);
    return true;
  }, [routeState]);

  // Auto-registration after login (replaceId = null) or a retry. The address is
  // the core's business: with no URL override the body carries no source_ip at all
  // and the core binds the registration to the connection it arrived on (D23).
  const runRegister = useCallback(async () => {
    const token = authRef.current;
    if (!token) return;
    setStep('registering');
    setRegisterError(null);
    try {
      const res = await captiveApi.register(
        { ...connectionFields(connection), replace_id: null, ...staticField() },
        token,
      );
      applyResult(res);
    } catch (err) {
      if (isSessionExpired(err)) {
        restartLogin();
        return;
      }
      if (err instanceof ApiError && err.code === 'captive.device_limit_reached') {
        setChoice(err.details as CaptiveAtLimitDetails);
        // Refresh the list from the live endpoint (falls back to the error payload).
        captiveApi
          .devices(token)
          .then((list) => setChoice((c) => ({ limit: c?.limit ?? 0, devices: list.items })))
          .catch(() => {
            /* keep the payload from the 409 */
          });
        setStep('choice');
        return;
      }
      const message = registerErrorMessage(err);
      // A refusal about the ADDRESS: drop the pick, re-read what is actually his and
      // put him back on the chooser with the reason. Gated on having made a pick, so
      // a build that never sends the field behaves exactly as before.
      if (
        err instanceof ApiError &&
        addressRefusals.has(err.code) &&
        selectedStaticRef.current != null
      ) {
        setSelectedStatic(null);
        selectedStaticRef.current = null;
        const again = await offerAddressChoice();
        if (again) {
          setAddressError(message);
          setStep('address');
          return;
        }
        // Nothing left to choose. Stay on the registration screen, but with the pick
        // cleared — so «Повторить» now registers DYNAMICALLY and succeeds, which is
        // what every one of these messages tells the subscriber to do.
        //
        // ⚠ AND IT RETURNS HERE RATHER THAN FALLING INTO THE D408 STATE FALLBACK
        // BELOW. A retry that WORKS beats a screen that routes: the dynamic
        // re-registration binds the device, and for a blocked subscriber that binding
        // is exactly what makes his access open by itself the moment he pays. The
        // fallback is for refusals a retry cannot fix; this one it can.
        setRegisterError(message);
        return;
      }
      // D408: a refusal this subscriber cannot register his way out of. If we already
      // know the account is blocked, THAT is what he needs to see — not a retry
      // button on a condition retrying cannot change.
      if (routeOnKnownBlockingState()) return;
      setRegisterError(message);
    }
  }, [
    addressRefusals,
    connection,
    applyResult,
    offerAddressChoice,
    registerErrorMessage,
    restartLogin,
    routeOnKnownBlockingState,
    staticField,
  ]);


  const handleLogin = useCallback(
    async (login: string, password: string) => {
      setLoginSubmitting(true);
      setLoginError(null);
      try {
        const { token, ...state } = await captiveApi.login({ login, password });
        const nextAuth = { token };
        authRef.current = nextAuth;
        setAuth(nextAuth);
        accountRef.current = state;
        setAccount(state);
        setActivationError(null);
        setTopUpNotice(null);
        // Nothing has been bound yet in THIS session (D408).
        setRegistrationConfirmed(false);
        // The address choice comes BEFORE the registration, because the registration
        // is what binds the address: `static_lease_id` rides on the same request and
        // the core binds it in that transaction. An account with no unused static
        // skips the screen entirely and the flow is unchanged.
        if (await offerAddressChoice()) {
          setStep('address');
          return;
        }
        // Register FIRST, whatever the state: a valid binding is what lets the next
        // authorization resolve this account at all. The core accepts a registration
        // from a non-serviceable account exactly for this reason.
        await runRegister();
      } catch (err) {
        if (err instanceof ApiError && err.isNetworkError) {
          setLoginError(t('login.networkError'));
        } else if (err instanceof ApiError && err.status === 401) {
          setLoginError(t('login.invalidCredentials'));
        } else if (err instanceof ApiError && err.status === 429) {
          setLoginError(t('login.tooManyAttempts'));
        } else {
          setLoginError(t('login.genericError'));
        }
      } finally {
        setLoginSubmitting(false);
      }
    },
    [offerAddressChoice, runRegister, t],
  );

  /** The subscriber picked a row (or went back to the dynamic default). */
  const handleSelectStatic = useCallback((leaseID: number | null) => {
    setSelectedStatic(leaseID);
    selectedStaticRef.current = leaseID;
    // The refusal on screen belonged to the PREVIOUS pick; leaving it up beside a
    // newly chosen address names the wrong one.
    setAddressError(null);
  }, []);

  /** «Продолжить» on the address screen — the registration the login used to run. */
  const handleAddressConfirm = useCallback(async () => {
    await runRegister();
  }, [runRegister]);

  const handleRetryRegister = useCallback(async () => {
    setRegisterRetrying(true);
    try {
      await runRegister();
    } finally {
      setRegisterRetrying(false);
    }
  }, [runRegister]);

  // Confirm a de-register choice: replace the selected device with the current
  // one (atomic de-register + register on the core).
  const handleChoiceConfirm = useCallback(
    async (deviceId: number) => {
      const token = authRef.current;
      if (!token) return;
      setChoiceSubmitting(true);
      setChoiceError(null);
      try {
        // ⚠ BOTH FIELDS TOGETHER. A subscriber at the device limit who also chose an
        // address is replacing a device AND naming where the new one lives; the two
        // are independent and the core binds them in one transaction. This body is
        // built separately from runRegister's, which is exactly why `staticField()`
        // exists — the previous version of this call site was the one place a
        // forgotten field would have silently reverted the choice to dynamic.
        const res = await captiveApi.register(
          { ...connectionFields(connection), replace_id: deviceId, ...staticField() },
          token,
        );
        applyResult(res);
      } catch (err) {
        if (isSessionExpired(err)) {
          restartLogin();
          return;
        }
        if (err instanceof ApiError && err.code === 'captive.device_limit_reached') {
          setChoice(err.details as CaptiveAtLimitDetails);
          setChoiceError(t('choice.confirmError'));
          return;
        }
        // D408, and it is here for the same reason it is in `runRegister`: this is
        // the SECOND call site of the same endpoint, and a fix applied to only one of
        // them is the seam-between-two-correct-halves shape this project keeps
        // producing. A blocked subscriber whose replacement registration is refused
        // would otherwise dead-end on the device chooser instead of the one screen
        // that tells him he is in arrears.
        if (routeOnKnownBlockingState()) return;
        setChoiceError(registerErrorMessage(err));
      } finally {
        setChoiceSubmitting(false);
      }
    },
    [
      connection,
      applyResult,
      registerErrorMessage,
      restartLogin,
      routeOnKnownBlockingState,
      staticField,
      t,
    ],
  );

  /**
   * Self-activation. On success the core opens the first billing cycle, charges the
   * quoted amount and disconnects the captive session, so the device asks for a
   * public address straight away → the "access is opening" confirmation.
   *
   * A 409 insufficient-balance is NOT a generic failure: it carries the live figures
   * at the moment of refusal, which replace what the screen was showing (the balance
   * may have moved since login) and name the exact shortfall.
   */
  /**
   * Load the plan list for an account that must choose one.
   *
   * Called when the activation screen is entered and after a refresh — deliberately
   * NOT on the 30 s poll, because each option costs the core an activation quote (a
   * dozen reads) and every device in the garden could be polling.
   *
   * ⚠ THE SELECTION IS PRESERVED ACROSS A RELOAD when the plan is still on offer. A
   * top-up refresh that silently cleared it would put the subscriber back at «выберите
   * тарифный план» the moment his money arrived — at exactly the moment he was about to
   * press the button.
   */
  const loadOptions = useCallback(async () => {
    const token = authRef.current;
    if (!token) return;
    setOptionsLoading(true);
    try {
      const res = await captiveApi.activationOptions(token);
      setOptions(res);
      setSelectedTariff((current) => {
        if (current != null && res.options.some((o) => o.tariff_id === current)) return current;
        // Auto-select a lone plan: a one-item radio group is a formality, and making the
        // subscriber tap it adds a step that can only be got wrong.
        return res.options.length === 1 ? res.options[0].tariff_id : null;
      });
    } catch {
      // A failed read leaves the screen with whatever it had. The plan list is not the
      // subscriber's only route — the top-up routes stay — and an error banner over a
      // transient fetch would read as «активация сломана».
    } finally {
      setOptionsLoading(false);
    }
  }, []);

  const handleSelectTariff = useCallback((tariffID: number) => {
    setSelectedTariff(tariffID);
    // The refusal that was on screen belonged to the PREVIOUS plan; leaving it up beside
    // a newly picked one would name the wrong figure.
    setActivationError(null);
  }, []);

  const handleActivate = useCallback(async () => {
    const token = authRef.current;
    if (!token) return;
    // Activating with no pick would send an empty body, which the core answers
    // `captive.tariff_choice_required` (409; a 500 before 2026-09-24) — correct, and a
    // round trip to say what the screen already knows.
    if (accountRef.current?.tariff_choice_required && selectedTariff == null) return;
    setActivating(true);
    setActivationError(null);
    try {
      const res = await captiveApi.activate(token, selectedTariff ?? undefined);
      const { activated, token: refreshed, ...state } = res;
      // The core mints a new token whose `blocked` claim reflects the activation;
      // keeping the old one would leave the session on the non-serviceable path.
      if (refreshed) {
        const nextAuth = { token: refreshed };
        authRef.current = nextAuth;
        setAuth(nextAuth);
      }
      accountRef.current = state;
      setAccount(state);
      // Defensive: only a state the core itself calls serviceable earns the "access
      // is opening" promise; anything else routes by what it reported (with the
      // figures from this very response).
      if (!activated || state.state !== 'serviceable') {
        routeState(state.state);
        return;
      }
      setConfirmVariant('activated');
      setStep('confirmed');
    } catch (err) {
      if (isSessionExpired(err)) {
        restartLogin();
        return;
      }
      if (err instanceof ApiError && err.code === 'captive.insufficient_balance') {
        const d = err.details as Partial<CaptiveInsufficientBalanceDetails> | undefined;
        const current = accountRef.current;
        const merged: CaptiveAccountState | null = current
          ? {
              ...current,
              balance: d?.balance ?? current.balance,
              required: d?.required ?? current.required,
              shortfall: d?.shortfall ?? current.shortfall,
              min_balance: d?.min_balance ?? current.min_balance,
              activation_charge: d?.activation_charge ?? current.activation_charge,
              can_activate: false,
            }
          : null;
        if (merged) {
          accountRef.current = merged;
          setAccount(merged);
        }
        // Name the exact missing amount; only if the core sent no figure at all
        // does this degrade to the generic failure copy.
        const missing = d?.shortfall ?? merged?.shortfall;
        setActivationError(
          isPositiveMoney(missing)
            ? t('activation.insufficientBalance', { amount: formatRubles(missing) })
            : t('activation.error'),
        );
        return;
      }
      if (err instanceof ApiError && err.code === 'captive.tariff_not_selectable') {
        // The picked plan was withdrawn (left the group, lost its price) between the
        // list and the click — nothing was assigned. Say so and RE-READ the list, which
        // drops the dead plan from the selection; «попробуйте ещё раз» would retry it.
        setActivationError(t('planChoice.withdrawn'));
        void loadOptions();
        return;
      }
      setActivationError(activationErrorMessage(err, 'activation.error'));
    } finally {
      setActivating(false);
    }
  }, [activationErrorMessage, loadOptions, restartLogin, routeState, selectedTariff, t]);

  /**
   * Re-read the account state with the token in hand — the button the subscriber
   * presses after paying in the office / at an ОСМП terminal, and the timer tick of
   * the activation screen. A state change (a manager activated the account, the
   * block was lifted) re-routes the flow.
   */
  const handleRefresh = useCallback(async () => {
    const token = authRef.current;
    if (!token) return;
    setRefreshing(true);
    try {
      const was = accountRef.current?.state;
      const state = await captiveApi.account(token);
      accountRef.current = state;
      setAccount(state);
      setActivationError(null);
      // A re-poll that finds a NEVER-ACTIVATED account now serviceable observed an
      // activation (a manager did it while the subscriber waited on this screen), so
      // it earns the activation copy rather than "device registered".
      routeState(state.state, was === 'awaiting_activation' ? 'activated' : 'registered');
      // The money moved, so every plan's verdict may have moved with it: a top-up is
      // exactly the event that turns «не хватает» into an activatable plan. Re-priced
      // here rather than on the poll, because THIS is the moment it changes.
      if (state.tariff_choice_required) void loadOptions();
    } catch (err) {
      if (isSessionExpired(err)) {
        restartLogin();
        return;
      }
      setActivationError(activationErrorMessage(err, 'common.unknownError'));
    } finally {
      setRefreshing(false);
    }
  }, [activationErrorMessage, loadOptions, restartLogin, routeState]);

  /**
   * Fetch the plan list the first time the activation screen is shown for an account
   * that must choose one.
   *
   * Keyed on the account id + the flag rather than on `step`, so re-entering the screen
   * for the SAME account does not re-quote every plan, while a re-login as somebody else
   * does.
   */
  useEffect(() => {
    if (step !== 'activation') return;
    if (!account?.tariff_choice_required) return;
    void loadOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, account?.account_id, account?.tariff_choice_required, loadOptions]);

  /**
   * Card top-up. Since Phase 3 the core starts a real PayOnline order and answers
   * `{provider, payment_id, redirect_url}`; the browser follows the redirect to the
   * acquirer's own secure form and comes back to this page, where the ordinary
   * account re-poll picks the credited balance up. There is deliberately no second
   * notification path — the server-to-server callback moves the balance and this
   * screen's existing refresh is what notices.
   *
   * `503 captive.topup_unavailable` is STILL possible — an installation with no
   * acquirer configured, or whose instance is switched off — and is still presented
   * as a calm notice pointing at the office/ОСМП routes, never as an error.
   */
  const handleTopUp = useCallback(async () => {
    const token = authRef.current;
    if (!token) return;
    const current = accountRef.current;
    // What to offer paying, in order: the activation shortfall, else what activation
    // requires, else the DEBT when the balance is negative — which is the whole
    // amount for a financially blocked subscriber, whose shortfall and requirement
    // are both zero. Without that last branch this screen would start a 0.00 top-up.
    //
    // The core FLOORS this at the account's own shortfall to the activation
    // threshold before it charges anything (Appendix C §2.8), so a figure that has
    // gone stale between two polls cannot start a top-up that lands short.
    const amount = isPositiveMoney(current?.shortfall)
      ? (current?.shortfall as string)
      : isPositiveMoney(current?.required)
        ? (current?.required as string)
        : isNegativeMoney(current?.balance)
          ? absMoney(current?.balance)
          : '0.00';
    setTopUpPending(true);
    setTopUpNotice(null);
    try {
      const intent = await captiveApi.topup({ amount, save_card: false }, token);
      if (intent?.redirect_url) {
        window.location.assign(intent.redirect_url);
        return;
      }
      setTopUpNotice(t('topup.unavailable'));
    } catch (err) {
      if (isSessionExpired(err)) {
        restartLogin();
        return;
      }
      setTopUpNotice(topUpNoticeMessage(err));
    } finally {
      setTopUpPending(false);
    }
  }, [restartLogin, t, topUpNoticeMessage]);

  const handleRelogin = useCallback(() => {
    // Return to the login screen for a different account; the connecting-device
    // identity read-out (context) is intentionally kept.
    authRef.current = null;
    setAuth(null);
    setStep('login');
    setLoginError(null);
    setRegisterError(null);
    setRegistrationConfirmed(false);
    setChoice(null);
    setChoiceError(null);
    setRegisteredDevice(undefined);
    // A different subscriber has different addresses — and MUST NOT inherit this
    // one's pick, which is a lease id the core would refuse as another account's.
    setStatics([]);
    setSelectedStatic(null);
    setAddressError(null);
    selectedStaticRef.current = null;
    accountRef.current = null;
    setAccount(null);
    setActivationError(null);
    setTopUpNotice(null);
    setConfirmVariant('registered');
  }, []);

  return (
    <PortalShell>
      {step === 'login' && (
        <LoginScreen
          context={context}
          // ⚠ KEYED ON THE MAC, NOT ON THE CONTEXT BEING NULL *(2026-08-22)*. Since
          // `/captive/context` answers 200 with `client_ip` and an empty `mac` where
          // it used to 404, a non-null context no longer proves the DEVICE was
          // identified — and `context === null` here would have silently dropped the
          // hint for every subscriber whose port was not detected, which is the
          // population this whole change exists for.
          identityUnavailable={contextResolved && !context?.mac}
          submitting={loginSubmitting}
          error={loginError}
          onSubmit={handleLogin}
        />
      )}
      {step === 'address' && (
        <AddressChoiceScreen
          addresses={statics}
          selected={selectedStatic}
          error={addressError}
          onSelect={handleSelectStatic}
          onConfirm={handleAddressConfirm}
        />
      )}
      {step === 'registering' && (
        <RegisteringScreen
          error={registerError}
          retrying={registerRetrying}
          onRetry={handleRetryRegister}
        />
      )}
      {step === 'choice' && (
        <DeviceChoiceScreen
          limit={choice?.limit ?? 0}
          devices={choice?.devices ?? []}
          submitting={choiceSubmitting}
          error={choiceError}
          onConfirm={handleChoiceConfirm}
        />
      )}
      {step === 'confirmed' && (
        <ConfirmationScreen
          device={registeredDevice}
          variant={confirmVariant}
          // D408: reachable from BlockedScreen's «Обновить» once the block is lifted,
          // on a session in which nothing was ever bound — so the screen must not
          // open with «Устройство зарегистрировано».
          registrationConfirmed={registrationConfirmed}
        />
      )}
      {step === 'activation' && account && (
        <ActivationScreen
          account={account}
          // D408: an `awaiting_activation` subscriber on an ISP address with no
          // captive lease reaches this screen with NOTHING bound, so its two
          // «устройство зарегистрировано» sentences must not be drawn.
          registrationConfirmed={registrationConfirmed}
          activating={activating}
          refreshing={refreshing}
          topUpPending={topUpPending}
          error={activationError}
          topUpNotice={topUpNotice}
          options={options}
          optionsLoading={optionsLoading}
          selectedTariff={selectedTariff}
          onSelectTariff={handleSelectTariff}
          onActivate={handleActivate}
          onTopUp={handleTopUp}
          onRefresh={handleRefresh}
          onRelogin={handleRelogin}
        />
      )}
      {step === 'blocked' && (
        <BlockedScreen
          account={account}
          // D408: this screen is now also reached from a FAILED registration, where
          // «устройство уже зарегистрировано» would be untrue.
          registrationConfirmed={registrationConfirmed}
          refreshing={refreshing}
          topUpPending={topUpPending}
          topUpNotice={topUpNotice}
          onTopUp={handleTopUp}
          onRefresh={handleRefresh}
          onRelogin={handleRelogin}
        />
      )}
      {step === 'support' && (
        <SupportScreen
          variant={supportVariant}
          // D408, same as BlockedScreen: reachable with nothing bound.
          registrationConfirmed={registrationConfirmed}
          onRelogin={handleRelogin}
        />
      )}
      {step === 'unavailable' && (
        <StateUnavailableScreen
          // D408, same reason: «Устройство зарегистрировано, но …» is the opening
          // clause of this screen's own body, and it is not always true any more.
          registrationConfirmed={registrationConfirmed}
          refreshing={refreshing}
          onRefresh={handleRefresh}
          onRelogin={handleRelogin}
        />
      )}
    </PortalShell>
  );
}
